import React,{useState,useEffect,useCallback,useMemo} from 'react';
import {expenseCategoryAPI,expenseAPI,branchAPI,uploadAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput} from '../components/common';
import {
  Plus,Edit,Trash2,CheckCircle,XCircle,Clock,
  Tag,Receipt,BarChart3,Building2,Upload,FileText,
  TrendingDown,ChevronDown,ChevronUp,Filter,Globe,
} from 'lucide-react';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import toast from 'react-hot-toast';

// ─── Helpers ─────────────────────────────────────────────────
const fmt=(n,d=2)=>Number(n||0).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d});
const today=()=>new Date().toISOString().slice(0,10);
const STATUS_BADGE={
  draft:   {v:'default',  l:'Draft'},
  approved:{v:'success',  l:'Approved'},
  rejected:{v:'danger',   l:'Rejected'},
};
const PM_OPTS=[
  {value:'cash',label:'Cash'},
  {value:'bank_transfer',label:'Bank Transfer'},
  {value:'card',label:'Card'},
  {value:'other',label:'Other'},
];

function ColorDot({color,size=12}){
  return <span style={{display:'inline-block',width:size,height:size,borderRadius:'50%',
    background:color||'#6366f1',flexShrink:0}}/>;
}
function StatCard({icon:Icon,label,value,sub,color='var(--primary)'}){
  return(
    <div className="card" style={{padding:'16px 20px',display:'flex',alignItems:'center',gap:14}}>
      <div style={{width:44,height:44,borderRadius:10,background:`${color}22`,display:'flex',
        alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <Icon size={20} color={color}/>
      </div>
      <div>
        <div style={{fontSize:11,color:'var(--td)',fontWeight:600,textTransform:'uppercase',letterSpacing:.5}}>{label}</div>
        <div style={{fontSize:20,fontWeight:800}}>{value}</div>
        {sub&&<div style={{fontSize:11,color:'var(--td)'}}>{sub}</div>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// CATEGORIES TAB
// ════════════════════════════════════════════════════════
function CategoriesTab(){
  const{isOwner,myBranchId,hasPermission}=useAuth();
  const canEdit=hasPermission('expenses.edit');
  const canCreate=hasPermission('expenses.create');
  const canDelete=hasPermission('expenses.delete');

  const[data,setData]=useState([]);
  const[branches,setBranches]=useState([]);
  const[search,setSearch]=useState('');
  const[filterBranch,setFilterBranch]=useState(myBranchId?String(myBranchId):'');
  const[modal,setModal]=useState(null);
  const[form,setForm]=useState({});
  const[delId,setDelId]=useState(null);

  const load=useCallback(async()=>{
    try{
      const params={search};
      // pass branch filter so backend scopes correctly
      if(isOwner&&filterBranch) params.branch_id=filterBranch;
      else if(!isOwner&&myBranchId) params.branch_id=myBranchId;
      const r=await expenseCategoryAPI.getAll(params);
      setData(r.data||[]);
    }catch{toast.error('Failed to load categories');}
  },[search,filterBranch,isOwner,myBranchId]);

  useEffect(()=>{
    if(isOwner) branchAPI.getAll({}).then(r=>setBranches(r.data?.data||r.data||[])).catch(()=>{});
  },[isOwner]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t);},[load]);

  const blank={name:'',description:'',color:'#6366f1',is_active:true,
    branch_id: myBranchId ? String(myBranchId) : ''};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{
    if(!form.name) return toast.error('Name is required');
    try{
      const payload={...form};
      // For owner: branch_id '' means global (null)
      if(isOwner) payload.branch_id=form.branch_id||null;
      if(modal==='new'){await expenseCategoryAPI.create(payload);toast.success('Category created');}
      else{await expenseCategoryAPI.update(form.id,payload);toast.success('Category updated');}
      setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||'Failed to save');}
  };
  const del=async()=>{
    try{await expenseCategoryAPI.delete(delId);toast.success('Deleted');setDelId(null);load();}
    catch(e){toast.error(e.response?.data?.error||'Failed to delete');}
  };

  const PRESET=['#6366f1','#22c55e','#f59e0b','#ef4444','#06b6d4','#8b5cf6','#ec4899','#14b8a6','#f97316','#64748b'];
  const branchOpts=[{value:'',label:'All Branches'},...branches.map(b=>({value:String(b.id),label:`${b.name} (${b.code})`}))];
  const branchFormOpts=[{value:'',label:'Global (all branches)'},...branches.map(b=>({value:String(b.id),label:`${b.name} (${b.code})`}))];

  const globalCats=data.filter(c=>!c.branch_id);
  const branchCats=data.filter(c=>!!c.branch_id);

  const CatCard=({cat})=>(
    <div className="card" style={{padding:'14px 16px',display:'flex',alignItems:'center',gap:12}}>
      <ColorDot color={cat.color} size={16}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:700,fontSize:14,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
          {cat.name}
          <Badge variant={cat.is_active?'success':'danger'}>{cat.is_active?'Active':'Inactive'}</Badge>
          {!cat.branch_id&&<span style={{fontSize:10,color:'var(--primary)',background:'var(--pm)',padding:'1px 6px',borderRadius:4,display:'flex',alignItems:'center',gap:3}}><Globe size={9}/> Global</span>}
          {cat.branch_id&&cat.branch&&<span style={{fontSize:10,color:'var(--td)',background:'var(--s2)',padding:'1px 6px',borderRadius:4,display:'flex',alignItems:'center',gap:3}}><Building2 size={9}/>{cat.branch.name}</span>}
        </div>
        {cat.description&&<div className="text-dim" style={{fontSize:11,marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cat.description}</div>}
      </div>
      {(canEdit||canDelete)&&(
        <div className="flex gap-1" style={{flexShrink:0}}>
          {canEdit&&<button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...cat,branch_id:cat.branch_id?String(cat.branch_id):''});setModal('edit')}}><Edit size={13}/></button>}
          {canDelete&&<button className="btn btn-danger btn-sm" onClick={()=>setDelId(cat.id)}><Trash2 size={13}/></button>}
        </div>
      )}
    </div>
  );

  return(<div>
    <div className="flex justify-between items-center" style={{marginBottom:14,gap:8,flexWrap:'wrap'}}>
      <div style={{flex:1,minWidth:200,maxWidth:380}}><SearchBar value={search} onChange={setSearch} placeholder="Search categories..."/></div>
      {isOwner&&(
        <select value={filterBranch} onChange={e=>setFilterBranch(e.target.value)}
          style={{padding:'7px 10px',borderRadius:7,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',fontSize:13}}>
          {branchOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {canCreate&&<button className="btn btn-primary" onClick={()=>{setForm({...blank});setModal('new')}}><Plus size={14}/> Add Category</button>}
    </div>

    {/* Global categories */}
    {globalCats.length>0&&(
      <div style={{marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5,marginBottom:8,display:'flex',alignItems:'center',gap:5}}>
          <Globe size={12}/> Global Categories
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10}}>
          {globalCats.map(c=><CatCard key={c.id} cat={c}/>)}
        </div>
      </div>
    )}

    {/* Branch-specific categories */}
    {branchCats.length>0&&(
      <div>
        <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5,marginBottom:8,display:'flex',alignItems:'center',gap:5}}>
          <Building2 size={12}/> Branch Categories
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10}}>
          {branchCats.map(c=><CatCard key={c.id} cat={c}/>)}
        </div>
      </div>
    )}

    {!data.length&&<div className="text-dim" style={{padding:40,textAlign:'center'}}>No categories yet. Add one to get started.</div>}

    {/* Create/Edit Modal */}
    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?'New Expense Category':'Edit Category'} width={480}>
      <FormInput label="Category Name *" value={form.name||''} onChange={v=>F('name',v)} required/>
      {isOwner&&(
        <FormInput label="Branch (empty = Global)" value={form.branch_id||''} onChange={v=>F('branch_id',v)} options={branchFormOpts}/>
      )}
      <FormInput label="Description" value={form.description||''} onChange={v=>F('description',v)} type="textarea"/>
      <div style={{marginBottom:12}}>
        <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--td)',marginBottom:6}}>Color</label>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          {PRESET.map(c=>(
            <button key={c} onClick={()=>F('color',c)} style={{width:28,height:28,borderRadius:'50%',background:c,
              border:form.color===c?'3px solid var(--text)':'2px solid transparent',cursor:'pointer',padding:0}}/>
          ))}
          <input type="color" value={form.color||'#6366f1'} onChange={e=>F('color',e.target.value)}
            style={{width:32,height:32,borderRadius:6,border:'1px solid var(--border)',cursor:'pointer',padding:2,background:'var(--surface)'}}/>
        </div>
      </div>
      <FormInput label="Status" value={form.is_active?'true':'false'} onChange={v=>F('is_active',v==='true')}
        options={[{value:'true',label:'Active'},{value:'false',label:'Inactive'}]}/>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Save</button>
      </div>
    </Modal>

    <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Category" width={380}>
      <p className="text-muted" style={{marginBottom:18}}>Delete this category? Expenses using it will be affected.</p>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setDelId(null)}>Cancel</button>
        <button className="btn btn-danger" onClick={del}>Delete</button>
      </div>
    </Modal>
  </div>);
}

// ════════════════════════════════════════════════════════
// EXPENSES TAB
// ════════════════════════════════════════════════════════
function ExpensesTab(){
  const{hasPermission,isOwner,myBranchId}=useAuth();
  const canApprove=hasPermission('expenses.approve');
  const canEdit=hasPermission('expenses.edit');
  const canDelete=hasPermission('expenses.delete');
  const canCreate=hasPermission('expenses.create');

  const[data,setData]=useState([]);
  const[total,setTotal]=useState(0);
  const[cats,setCats]=useState([]);
  const[branches,setBranches]=useState([]);
  const[ld,setLd]=useState(true);
  const[search,setSearch]=useState('');
  const[filters,setFilters]=useState({
    branch_id:(!isOwner&&myBranchId)?String(myBranchId):'',
    category_id:'',status:'',from_date:'',to_date:'',
  });
  const[showFilter,setShowFilter]=useState(false);
  const[page,setPage]=useState(1);
  const[modal,setModal]=useState(null);
  const[form,setForm]=useState({});
  const[delId,setDelId]=useState(null);
  const[uploading,setUploading]=useState(false);
  const LIMIT=25;

  // Load categories scoped to the selected/user branch
  const loadCats=useCallback(async(branchId)=>{
    try{
      const params={};
      const bid=branchId||(isOwner?null:myBranchId);
      if(bid) params.branch_id=bid;
      const r=await expenseCategoryAPI.getAll(params);
      setCats(r.data||[]);
    }catch{}
  },[isOwner,myBranchId]);

  const loadMeta=async()=>{
    try{
      const bR=await branchAPI.getAll({});
      setBranches(bR.data?.data||bR.data||[]);
    }catch{}
    loadCats(isOwner?null:myBranchId);
  };

  const load=useCallback(async()=>{
    setLd(true);
    try{
      const p={search,page,limit:LIMIT,...filters};
      Object.keys(p).forEach(k=>{if(p[k]==='')delete p[k];});
      const r=await expenseAPI.getAll(p);
      setData(r.data.data||[]);setTotal(r.data.total||0);
    }catch{toast.error('Failed to load expenses');}
    finally{setLd(false);}
  },[search,page,filters]);

  useEffect(()=>{loadMeta();},[]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t);},[load]);

  // When owner changes branch in form, reload cats for that branch
  const handleFormBranchChange=(v)=>{
    F('branch_id',v);
    F('category_id',''); // reset category
    loadCats(v||null);
  };

  const blank={
    branch_id: myBranchId?String(myBranchId):'',
    category_id:'',title:'',description:'',
    amount:'',currency:'USD',exchange_rate:1,
    expense_date:today(),payment_method:'cash',
    receipt_url:'',notes:'',
  };
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{
    if(!form.branch_id) return toast.error('Branch is required');
    if(!form.category_id) return toast.error('Category is required');
    if(!form.title) return toast.error('Title is required');
    if(!form.amount||isNaN(form.amount)) return toast.error('Valid amount is required');
    try{
      if(modal==='new'){await expenseAPI.create(form);toast.success('Expense created');}
      else{await expenseAPI.update(form.id,form);toast.success('Expense updated');}
      setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||'Failed to save');}
  };

  const changeStatus=async(id,status)=>{
    try{await expenseAPI.updateStatus(id,status);toast.success(status==='approved'?'Approved':'Rejected');load();}
    catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  const del=async()=>{
    try{await expenseAPI.delete(delId);toast.success('Deleted');setDelId(null);load();}
    catch(e){toast.error(e.response?.data?.error||'Failed to delete');}
  };

  const uploadReceipt=async(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    if(file.size>10*1024*1024) return toast.error('File too large (max 10MB)');
    setUploading(true);
    try{const r=await uploadAPI.document(file);F('receipt_url',r.data.url);toast.success('Receipt uploaded');}
    catch{toast.error('Upload failed');}
    finally{setUploading(false);}
  };

  // All options — use String(id) for value to avoid number/string mismatch
  const catOpts=[{value:'',label:'All Categories'},...cats.map(c=>({value:String(c.id),label:c.name}))];
  const branchOpts=[{value:'',label:'All Branches'},...branches.map(b=>({value:String(b.id),label:`${b.name} (${b.code})`}))];
  const catFormOpts=[{value:'',label:'Select category...'},...cats.map(c=>({value:String(c.id),label:c.name}))];
  const branchFormOpts=isOwner
    ?[{value:'',label:'Select branch...'},...branches.map(b=>({value:String(b.id),label:`${b.name} (${b.code})`}))]
    :branches.filter(b=>b.id===myBranchId).map(b=>({value:String(b.id),label:`${b.name} (${b.code})`}));

  // Find branch name for locked display
  const myBranchName=useMemo(()=>{
    const b=branches.find(b=>b.id===myBranchId);
    return b?`${b.name} (${b.code})`:'My Branch';
  },[branches,myBranchId]);

  const columns=[
    {key:'expense_number',label:'#',render:r=><span style={{fontFamily:'monospace',fontSize:12,color:'var(--td)'}}>{r.expense_number}</span>},
    {key:'expense_date',label:'Date',render:r=><span style={{fontSize:12}}>{r.expense_date}</span>},
    {key:'branch',label:'Branch',render:r=>(
      <div style={{display:'flex',alignItems:'center',gap:5}}>
        <Building2 size={12} color="var(--td)"/>
        <span style={{fontSize:12}}>{r.branch?.name||'—'}</span>
      </div>
    )},
    {key:'category',label:'Category',render:r=>{
      const cat=r.category;
      return cat?<div style={{display:'flex',alignItems:'center',gap:5}}>
        <ColorDot color={cat.color} size={10}/><span style={{fontSize:12}}>{cat.name}</span>
      </div>:'—';
    }},
    {key:'title',label:'Title',render:r=><span style={{fontWeight:500,fontSize:13}}>{r.title}</span>},
    {key:'amount',label:'Amount',render:r=>(
      <div>
        <div style={{fontWeight:700,fontSize:13}}>${fmt(r.amount_usd)}</div>
        {r.currency!=='USD'&&<div style={{fontSize:10,color:'var(--td)'}}>{r.currency} {fmt(r.amount)}</div>}
      </div>
    )},
    {key:'payment_method',label:'Payment',render:r=><span style={{fontSize:11,textTransform:'capitalize'}}>{(r.payment_method||'').replace('_',' ')}</span>},
    {key:'status',label:'Status',render:r=><Badge variant={STATUS_BADGE[r.status]?.v||'default'}>{STATUS_BADGE[r.status]?.l||r.status}</Badge>},
    {key:'actions',label:'',render:r=>(
      <div className="flex gap-1" style={{justifyContent:'flex-end'}}>
        {canApprove&&r.status==='draft'&&<>
          <button className="btn btn-ghost btn-sm" title="Approve" onClick={()=>changeStatus(r.id,'approved')}><CheckCircle size={14} color="var(--success)"/></button>
          <button className="btn btn-ghost btn-sm" title="Reject"  onClick={()=>changeStatus(r.id,'rejected')}><XCircle size={14} color="var(--danger)"/></button>
        </>}
        {canEdit&&r.status!=='approved'&&(
          <button className="btn btn-ghost btn-sm" onClick={()=>{
            // reload cats for the expense's branch before opening modal
            loadCats(r.branch_id);
            setForm({...r,branch_id:String(r.branch_id||''),category_id:String(r.category_id||'')});
            setModal('edit');
          }}><Edit size={13}/></button>
        )}
        {r.receipt_url&&<a href={r.receipt_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="Receipt"><FileText size={13}/></a>}
        {canDelete&&r.status!=='approved'&&<button className="btn btn-danger btn-sm" onClick={()=>setDelId(r.id)}><Trash2 size={13}/></button>}
      </div>
    )},
  ];

  const totalPages=Math.ceil(total/LIMIT);

  return(<div>
    {/* Filter bar */}
    <div style={{marginBottom:14}}>
      <div className="flex gap-2" style={{flexWrap:'wrap',alignItems:'center'}}>
        <div style={{flex:1,minWidth:200,maxWidth:340}}><SearchBar value={search} onChange={v=>{setSearch(v);setPage(1);}} placeholder="Search expenses..."/></div>
        <button className="btn btn-ghost btn-sm" onClick={()=>setShowFilter(f=>!f)} style={{display:'flex',alignItems:'center',gap:5}}>
          <Filter size={13}/> Filters {showFilter?<ChevronUp size={12}/>:<ChevronDown size={12}/>}
        </button>
        {canCreate&&<button className="btn btn-primary" onClick={()=>{setForm({...blank});loadCats(myBranchId||null);setModal('new')}}><Plus size={14}/> Add Expense</button>}
      </div>

      {showFilter&&(
        <div className="card" style={{padding:'12px 14px',marginTop:8,display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:10}}>
          {isOwner&&<FormInput label="Branch" value={filters.branch_id} onChange={v=>{setFilters(f=>({...f,branch_id:v,category_id:''}));loadCats(v||null);}} options={branchOpts}/>}
          <FormInput label="Category" value={filters.category_id} onChange={v=>setFilters(f=>({...f,category_id:v}))} options={catOpts}/>
          <FormInput label="Status" value={filters.status} onChange={v=>setFilters(f=>({...f,status:v}))}
            options={[{value:'',label:'All'},{value:'draft',label:'Draft'},{value:'approved',label:'Approved'},{value:'rejected',label:'Rejected'}]}/>
          <FormInput label="From Date" value={filters.from_date} onChange={v=>setFilters(f=>({...f,from_date:v}))} type="date"/>
          <FormInput label="To Date"   value={filters.to_date}   onChange={v=>setFilters(f=>({...f,to_date:v}))}   type="date"/>
          <div style={{display:'flex',alignItems:'flex-end'}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>{
              setFilters({branch_id:(!isOwner&&myBranchId)?String(myBranchId):'',category_id:'',status:'',from_date:'',to_date:''});
              loadCats(isOwner?null:myBranchId);
            }}>Clear</button>
          </div>
        </div>
      )}
    </div>

    <DataTable columns={columns} data={data} loading={ld} emptyMessage="No expenses found"/>

    {totalPages>1&&(
      <div className="flex justify-between items-center" style={{marginTop:12,fontSize:12,color:'var(--td)'}}>
        <span>{total} total</span>
        <div className="flex gap-1">
          {Array.from({length:Math.min(totalPages,7)},(_,i)=>i+1).map(p=>(
            <button key={p} className={`btn btn-sm ${p===page?'btn-primary':'btn-ghost'}`} onClick={()=>setPage(p)}>{p}</button>
          ))}
        </div>
      </div>
    )}

    {/* Create/Edit Modal */}
    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?'New Expense':'Edit Expense'} width={560}>
      <div className="form-grid">
        {/* Branch field */}
        {isOwner
          ?<FormInput label="Branch *" value={String(form.branch_id||'')} onChange={handleFormBranchChange} options={branchFormOpts}/>
          :<div style={{marginBottom:12}}>
            <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--td)',marginBottom:6}}>Branch</label>
            <div style={{padding:'8px 12px',background:'var(--s2)',borderRadius:6,border:'1px solid var(--border)',fontSize:13,display:'flex',alignItems:'center',gap:6}}>
              <Building2 size={13} color="var(--td)"/>{myBranchName}
            </div>
          </div>
        }
        {/* Category — only shows cats relevant to chosen branch */}
        <FormInput label="Category *" value={String(form.category_id||'')} onChange={v=>F('category_id',v)} options={catFormOpts}/>
      </div>
      <FormInput label="Title *" value={form.title||''} onChange={v=>F('title',v)} placeholder="e.g. Office Supplies"/>
      <div className="form-grid">
        <FormInput label="Amount *" value={String(form.amount||'')} onChange={v=>F('amount',v)} type="number" placeholder="0.00"/>
        <FormInput label="Currency" value={form.currency||'USD'} onChange={v=>F('currency',v)}
          options={[{value:'USD',label:'USD'},{value:'KHR',label:'KHR'}]}/>
      </div>
      {form.currency!=='USD'&&(
        <FormInput label="Exchange Rate (1 USD = ?)" value={String(form.exchange_rate||1)} onChange={v=>F('exchange_rate',v)} type="number"/>
      )}
      <div className="form-grid">
        <FormInput label="Date *" value={form.expense_date||today()} onChange={v=>F('expense_date',v)} type="date"/>
        <FormInput label="Payment Method" value={form.payment_method||'cash'} onChange={v=>F('payment_method',v)} options={PM_OPTS}/>
      </div>
      <FormInput label="Description" value={form.description||''} onChange={v=>F('description',v)} type="textarea"/>

      {/* Receipt Upload */}
      <div style={{marginBottom:12}}>
        <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--td)',marginBottom:6}}>Receipt</label>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {form.receipt_url
            ?<>
              <a href={form.receipt_url} target="_blank" rel="noreferrer" style={{fontSize:12,color:'var(--primary)',display:'flex',alignItems:'center',gap:4}}>
                <FileText size={14}/> View Receipt
              </a>
              <button className="btn btn-ghost btn-sm" onClick={()=>F('receipt_url','')}>Remove</button>
            </>
            :<label className="btn btn-ghost btn-sm" style={{cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>
              <Upload size={13}/>{uploading?'Uploading...':'Upload Receipt'}
              <input type="file" accept="image/*,.pdf" onChange={uploadReceipt} style={{display:'none'}} disabled={uploading}/>
            </label>
          }
        </div>
      </div>

      <FormInput label="Notes" value={form.notes||''} onChange={v=>F('notes',v)} type="textarea"/>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Save</button>
      </div>
    </Modal>

    <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Expense" width={380}>
      <p className="text-muted" style={{marginBottom:18}}>Delete this expense record? This cannot be undone.</p>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setDelId(null)}>Cancel</button>
        <button className="btn btn-danger" onClick={del}>Delete</button>
      </div>
    </Modal>
  </div>);
}

// ════════════════════════════════════════════════════════
// REPORT TAB
// ════════════════════════════════════════════════════════
function ReportTab(){
  const{isOwner,myBranchId}=useAuth();
  const[data,setData]=useState(null);
  const[ld,setLd]=useState(false);
  const[branches,setBranches]=useState([]);
  const[cats,setCats]=useState([]);
  const[filters,setFilters]=useState({
    from_date:'',to_date:'',
    branch_id:(!isOwner&&myBranchId)?String(myBranchId):'',
    category_id:'',
  });

  const load=useCallback(async()=>{
    setLd(true);
    try{
      const p={...filters};
      Object.keys(p).forEach(k=>{if(!p[k])delete p[k];});
      const r=await expenseAPI.report(p);
      setData(r.data);
    }catch{toast.error('Failed to load report');}
    finally{setLd(false);}
  },[filters]);

  useEffect(()=>{
    Promise.all([branchAPI.getAll({}),expenseCategoryAPI.getAll({})])
      .then(([bR,cR])=>{setBranches(bR.data?.data||bR.data||[]);setCats(cR.data||[]);})
      .catch(()=>{});
    load();
  },[]);

  const branchOpts=[{value:'',label:'All Branches'},...branches.map(b=>({value:String(b.id),label:`${b.name} (${b.code})`}))];
  const catOpts=[{value:'',label:'All Categories'},...cats.map(c=>({value:String(c.id),label:c.name}))];

  const grandTotal=parseFloat(data?.totals?.grand_total||0);
  const grandCount=parseInt(data?.totals?.grand_count||0);

  return(<div>
    <div className="card" style={{padding:'12px 14px',marginBottom:14,display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:10,alignItems:'flex-end'}}>
      {isOwner&&<FormInput label="Branch" value={filters.branch_id} onChange={v=>setFilters(f=>({...f,branch_id:v}))} options={branchOpts}/>}
      <FormInput label="Category" value={filters.category_id} onChange={v=>setFilters(f=>({...f,category_id:v}))} options={catOpts}/>
      <FormInput label="From Date" value={filters.from_date} onChange={v=>setFilters(f=>({...f,from_date:v}))} type="date"/>
      <FormInput label="To Date"   value={filters.to_date}   onChange={v=>setFilters(f=>({...f,to_date:v}))}   type="date"/>
      <button className="btn btn-primary" onClick={load} disabled={ld}>{ld?'Loading...':'Apply'}</button>
    </div>

    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:16}}>
      <StatCard icon={TrendingDown} label="Total Expenses" value={`$${fmt(grandTotal)}`} sub={`${grandCount} records`} color="var(--danger)"/>
      <StatCard icon={Building2}   label="Branches"        value={data?.byBranch?.length||0}   sub="reporting branches" color="var(--primary)"/>
      <StatCard icon={Tag}         label="Categories"      value={data?.byCategory?.length||0}  sub="active categories"  color="#f59e0b"/>
    </div>

    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
      {/* By Branch */}
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:13,display:'flex',alignItems:'center',gap:6}}>
          <Building2 size={14} color="var(--primary)"/> By Branch
        </div>
        <div style={{padding:'8px 0'}}>
          {data?.byBranch?.length
            ?data.byBranch.map(row=>{
              const total=parseFloat(row.dataValues?.total||row.total||0);
              const pct=grandTotal>0?(total/grandTotal*100):0;
              return(
                <div key={row.branch_id} style={{padding:'8px 16px',borderBottom:'1px solid var(--border)'}}>
                  <div className="flex justify-between items-center" style={{marginBottom:4}}>
                    <span style={{fontWeight:600,fontSize:13}}>{row.branch?.name||'Unknown'}</span>
                    <span style={{fontWeight:700,fontSize:13}}>${fmt(total)}</span>
                  </div>
                  <div style={{height:4,background:'var(--s2)',borderRadius:2,overflow:'hidden'}}>
                    <div style={{height:'100%',background:'var(--primary)',width:`${pct}%`,transition:'width .3s'}}/>
                  </div>
                  <div style={{fontSize:10,color:'var(--td)',marginTop:2}}>{parseInt(row.dataValues?.count||row.count||0)} expenses · {pct.toFixed(1)}%</div>
                </div>
              );
            })
            :<div className="text-dim" style={{padding:30,textAlign:'center'}}>No data</div>}
        </div>
      </div>

      {/* By Category */}
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:13,display:'flex',alignItems:'center',gap:6}}>
          <Tag size={14} color="#f59e0b"/> By Category
        </div>
        <div style={{padding:'8px 0'}}>
          {data?.byCategory?.length
            ?data.byCategory.map(row=>{
              const cat=row.category;
              const total=parseFloat(row.dataValues?.total||row.total||0);
              const pct=grandTotal>0?(total/grandTotal*100):0;
              return(
                <div key={row.category_id} style={{padding:'8px 16px',borderBottom:'1px solid var(--border)'}}>
                  <div className="flex justify-between items-center" style={{marginBottom:4}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <ColorDot color={cat?.color} size={10}/>
                      <span style={{fontWeight:600,fontSize:13}}>{cat?.name||'Unknown'}</span>
                    </div>
                    <span style={{fontWeight:700,fontSize:13}}>${fmt(total)}</span>
                  </div>
                  <div style={{height:4,background:'var(--s2)',borderRadius:2,overflow:'hidden'}}>
                    <div style={{height:'100%',background:cat?.color||'var(--primary)',width:`${pct}%`,transition:'width .3s'}}/>
                  </div>
                  <div style={{fontSize:10,color:'var(--td)',marginTop:2}}>{parseInt(row.dataValues?.count||row.count||0)} expenses · {pct.toFixed(1)}%</div>
                </div>
              );
            })
            :<div className="text-dim" style={{padding:30,textAlign:'center'}}>No data</div>}
        </div>
      </div>
    </div>

    {/* Monthly trend */}
    {data?.trend?.length>0&&(
      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',fontWeight:700,fontSize:13,display:'flex',alignItems:'center',gap:6}}>
          <BarChart3 size={14} color="var(--primary)"/> Monthly Trend by Branch
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'var(--s2)'}}>
                <th style={{padding:'8px 16px',textAlign:'left',fontWeight:600,color:'var(--td)',whiteSpace:'nowrap'}}>Month</th>
                {[...new Map(data.trend.map(r=>[r.branch_id,r.branch])).values()].map(b=>(
                  <th key={b?.id} style={{padding:'8px 16px',textAlign:'right',fontWeight:600,color:'var(--td)',whiteSpace:'nowrap'}}>{b?.name||'—'}</th>
                ))}
                <th style={{padding:'8px 16px',textAlign:'right',fontWeight:700}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {[...new Set(data.trend.map(r=>String(r.dataValues?.month||r.month)))].map(month=>{
                const rows=data.trend.filter(r=>String(r.dataValues?.month||r.month)===month);
                const branchIds=[...new Map(data.trend.map(r=>[r.branch_id,r.branch])).keys()];
                const rowTotal=rows.reduce((s,r)=>s+parseFloat(r.dataValues?.total||r.total||0),0);
                return(
                  <tr key={month} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'8px 16px',fontWeight:600}}>{month?new Date(month).toLocaleDateString('en-US',{year:'numeric',month:'short'}):'—'}</td>
                    {branchIds.map(bid=>{
                      const row=rows.find(r=>r.branch_id===bid);
                      return<td key={bid} style={{padding:'8px 16px',textAlign:'right'}}>{row?`$${fmt(row.dataValues?.total||row.total)}`:'—'}</td>;
                    })}
                    <td style={{padding:'8px 16px',textAlign:'right',fontWeight:700}}>${fmt(rowTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>);
}

// ════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════
const TABS=[
  {key:'expenses',  label:'Expenses',   icon:Receipt},
  {key:'categories',label:'Categories', icon:Tag},
  {key:'report',    label:'Report',     icon:BarChart3},
];

export default function ExpensesPage(){
  const[tab,setTab]=useState('expenses');
  return(<div className="page-wrapper">
    <PageHeader title="Expenses" subtitle="Track and manage business expenses by branch"/>
    <div style={{display:'flex',gap:4,marginBottom:18,borderBottom:'1px solid var(--border)'}}>
      {TABS.map(({key,label,icon:Icon})=>(
        <button key={key} onClick={()=>setTab(key)} style={{
          display:'flex',alignItems:'center',gap:6,padding:'8px 16px',
          border:'none',background:'transparent',cursor:'pointer',
          color:tab===key?'var(--primary)':'var(--tm)',
          fontWeight:tab===key?700:500,fontSize:13,
          borderBottom:tab===key?'2px solid var(--primary)':'2px solid transparent',
          marginBottom:-1,transition:'all .12s',
        }}>
          <Icon size={14}/>{label}
        </button>
      ))}
    </div>
    {tab==='expenses'  &&<ExpensesTab/>}
    {tab==='categories'&&<CategoriesTab/>}
    {tab==='report'    &&<ReportTab/>}
  </div>);
}
